import React, { useMemo } from 'react';
import useInternalProjects, { ProjectsResponse } from '../../hooks/useInternalProjects';

import { useMutation } from '@tanstack/react-query';
import { archiveProject } from '../../services/projectService';
import { Project } from '../../utils/models';

export interface PaginationResponse<T> {
	dataLength: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	projectsLoading: boolean;
	totalProjects: number;
	pages: T[];
	fetchNextPage: () => void;
	fetchPreviousPage: () => void;
}

export interface ProjectProps extends PaginationResponse<ProjectsResponse> {
	archiveError: Error | null;
	projectsLastUpdated: number | null;
	projectArchiving: boolean;
	projectsError: Error | null;
	refreshProjects: () => Promise<void>;
	onArchiveProject: (projectId: string) => void;
	projects: Project[];
}

export default function withProjects(Component: any): React.FC<any> {
	return function WithProjects(props): JSX.Element {
		const { data, error, dataUpdatedAt, fetchNextPage, fetchPreviousPage, isFetching, hasPreviousPage, hasNextPage, refetch } = useInternalProjects();
		const { mutate, error: archiveError, isPending: isArchiving } = useMutation({ 
			mutationFn: (projectId: string) => archiveProject(projectId),
			onSuccess: () => refetch(),
		});

		const fetchedCount = useMemo(() => {
			return data.pages.reduce((total, page) => total + page.projects.length, 0) || 0;
		}, [data]);

		const totalProjects = useMemo(() => {
			return data.pages[0].totalProjects;
		}, [data]);

		const projects = useMemo(() => {
			return data.pages.flatMap((page) => page.projects);
		}, [data.pages]);

		return (
			<Component
				dataLength={fetchedCount}
				pages={data.pages}
				hasNextPage={!!hasNextPage}
				hasPreviousPage={!!hasPreviousPage}
				fetchNextPage={() => fetchNextPage()}
				fetchPreviousPage={() => fetchPreviousPage()}
				projectsLoading={isFetching}
				refreshProjects={refetch}
				totalProjects={totalProjects}
				projects={projects}
				
				onArchiveProject={mutate}
				projectArchiving={isArchiving}
				archiveError={archiveError}
				projectsError={error}
				projectsLastUpdated={dataUpdatedAt}

				{...props}
			/>
		);
	};
}
